import React from 'react';
import { UserGroupOutlined } from '@duxton-web/icons';
import { Popover, Tag, Timeline, Typography } from '@duxton-web/ui';
import styled from 'styled-components';

import { ExperimentState } from '../../constants';
import { useExperimentContext } from '../../contexts/experiment-context';
import { GRABX_SLACK_CHANNEL } from '@/constants';
import { ApprovalFlowNode } from '@/features/experiment/domain';
import { useGetApprovers } from '@/hooks';
import { defaultPresetColors } from '@/theme';
import { formatUTCStringToDateTimeString } from '@/utils/dateTime';

const enum ApprovalFlowViewModelActionStr {
  PENDING = 'Pending Approval',
}

const Dot = ({ background }: { background?: string }) => {
  return (
    <div style={{ width: 10, height: 10, background, borderRadius: 100 }} />
  );
};

const Node = ({
  node,
  isLoading,
  approvers,
}: {
  node: ApprovalFlowNode;
  isLoading: boolean;
  approvers: string[];
}) => {
  const experimentContext = useExperimentContext();
  const { experiment, variableTechFamilies } = experimentContext;
  const experimentStartTime = experiment?.startTime;

  const { email, actionStr, resource } = node;

  function renderApprovers() {
    const { email, actionStr, resource } = node;

    if (
      actionStr !== ApprovalFlowViewModelActionStr.PENDING ||
      (email && resource)
    )
      return null;

    if (isLoading) return null;

    if (!approvers.length)
      return (
        <Tag
          id="workflow-no-users"
          color="yellow"
          style={{
            width: 'fit-content',
          }}
          size="small"
        >
          No users able to approve this experiment. Please reach out to
          @oncall-experimentation-platform in{' '}
          <Typography.Link href={GRABX_SLACK_CHANNEL}>#grab-x</Typography.Link>
        </Tag>
      );

    const usersTag = approvers?.map((user) => (
      <Tag
        color="white"
        key={user}
        style={{
          margin: '2px 0',
        }}
      >
        {user}
      </Tag>
    ));

    const usersTagContainer = (
      <div
        style={{
          display: 'flex',
          gap: 4,
          flexWrap: 'wrap',
          maxWidth: 700,
          maxHeight: 300,
          overflow: 'hidden scroll',
        }}
      >
        {usersTag}
      </div>
    );

    return (
      <Popover content={usersTagContainer}>
        <Tag
          icon={<UserGroupOutlined />}
          color="white"
          style={{
            width: 'fit-content',
            cursor: 'pointer',
          }}
        >{`${variableTechFamilies.join(',')} (${approvers?.length} users)`}</Tag>
      </Popover>
    );
  }

  function renderFlowDescription() {
    const { actionStr, timestamp, comments } = node;

    if (actionStr === ApprovalFlowViewModelActionStr.PENDING) {
      return `Experiment will be cancelled if not approved by ${formatUTCStringToDateTimeString(
        experimentStartTime?.toString() || '',
      )}`;
    }

    return `${formatUTCStringToDateTimeString(timestamp)} - ${
      comments ? `Comment: ${comments}` : 'No Comment'
    }`;
  }

  let title = (
    <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
      <Typography.Text strong>{email}</Typography.Text>
      <Typography.Text>{actionStr.toLowerCase()}</Typography.Text>
    </div>
  );

  if (
    actionStr === ApprovalFlowViewModelActionStr.PENDING &&
    !email &&
    !resource
  ) {
    title = (
      <Typography.Text>
        Pending approval from the following tech family
      </Typography.Text>
    );
  }

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
      }}
    >
      {title}
      {resource && (
        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Overlap with: {resource}
        </Typography.Text>
      )}
      {renderApprovers()}
      <Typography.Text type="secondary" style={{ fontSize: 12 }}>
        {renderFlowDescription()}
      </Typography.Text>
    </div>
  );
};

const ApprovalFlowView = () => {
  const experimentContext = useExperimentContext();
  const { experiment, variableTechFamilies } = experimentContext;

  const { data, isLoading } = useGetApprovers(
    {
      techFamilies: variableTechFamilies,
    },
    {
      enabled: experiment?.state === ExperimentState.REQUESTED,
    },
  );

  // Guard against null experiment during loading/refresh
  if (!experiment) {
    return null;
  }

  const approvalFlowItems =
    experiment.approvalFlow?.data?.map((d) => {
      return {
        dot: <Dot background={defaultPresetColors.green} />,
        color: '',
        children: (
          <Node
            node={d}
            isLoading={isLoading}
            approvers={data?.data?.users || []}
          />
        ),
      };
    }) || [];

  const items = [
    {
      dot: <Dot background={defaultPresetColors.green} />,
      color: '',
      children: (
        <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
          <Typography.Text strong>{experiment.owner}</Typography.Text>
          <Typography.Text>requested approval</Typography.Text>
        </div>
      ),
    },
    ...approvalFlowItems,
  ];

  return (
    <BorderBox>
      <Timeline items={items} />
    </BorderBox>
  );
};

export default ApprovalFlowView;

const BorderBox = styled.div`
  border: 1px solid #e8e8e8;
  padding: 16px 16px 0px;
  margin: 16px 32px 64px;
  border-radius: 8px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background: #ffffff;
  border: none;
  box-shadow: 0 4px 9px 0 rgb(22 26 29 / 5%);
`;
